import { FC, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Group,
  Text,
  Stack,
  TextInput,
  Select,
  Alert,
  Loader,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { IconPlus, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  useAccessPolicies,
  useCreateInternalUser,
  type InternalUserFormData,
} from '../hooks';
import FormActionButtons, { FormActionButtonsRef } from '@/components/FormActionButtons';
import { isEqual } from 'lodash';

//------------------------------------------------------------------------------
interface CreateInternalUserModalProps {
  onUserCreated?: () => void;
}

//------------------------------------------------------------------------------
const CreateInternalUserModal: FC<CreateInternalUserModalProps> = ({
  onUserCreated,
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation(['admin', 'common']);

  // Use our custom hooks
  const { accessPolicies, loading: loadingPolicies } =
    useAccessPolicies(opened);
  const { createUser, loading } = useCreateInternalUser(onUserCreated);
  const form = useForm<InternalUserFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      specialization: '',
      role: 'practitioner',
      accessPolicy: '',
    },
    validate: {
      firstName: (value) => (!value ? 'First name is required' : null),
      lastName: (value) => (!value ? 'Last name is required' : null),
      email: (value) => {
        if (!value) return 'Email address is required';
        if (!/^\S+@\S+\.\S+$/.test(value))
          return 'Please enter a valid email address';
        return null;
      },
      role: (value) => (!value ? 'System role is required' : null),
    },
  });
  const baselineRef = useRef<InternalUserFormData | null>(null);
  const formActionsRef = useRef<FormActionButtonsRef>(null);

  useEffect(() => {
    if (opened) {
      baselineRef.current = { ...(form.getValues() as InternalUserFormData) };
    } else {
      baselineRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(form.getValues(), baselineRef.current);
    } catch {
      return true;
    }
  };

  const handleClose = () => {
    close();
    form.reset();
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      form.setValues(baselineRef.current);
    } else {
      form.reset();
    }
  };

  // ---------------------------------------------------------------------------
  const handleSubmit = async (values: InternalUserFormData) => {
    const success = await createUser(values);
    if (success) {
      close();
      form.reset();
    }
  };

  // ---------------------------------------------------------------------------
  return (
    <>
      <Button
        leftSection={<IconPlus size={16} />}
        onClick={open}
        variant="filled"
      >
        {t('userManagement.createUser.buttonText', { ns: 'admin' })}
      </Button>
      <Modal
        opened={opened}
        onClose={() => formActionsRef.current?.handleGuardedClose()}
        title={t('userManagement.createUser.title', { ns: 'admin' })}
        size="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          <Stack gap="md">
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              {t('userManagement.createUser.infoMessage', { ns: 'admin' })}
            </Alert>
            <Group grow>
              <TextInput
                label={t('userManagement.createUser.firstName', {
                  ns: 'admin',
                })}
                placeholder={t(
                  'userManagement.createUser.firstNamePlaceholder',
                  { ns: 'admin' }
                )}
                required
                {...form.getInputProps('firstName')}
              />
              <TextInput
                label={t('userManagement.createUser.lastName', { ns: 'admin' })}
                placeholder={t(
                  'userManagement.createUser.lastNamePlaceholder',
                  { ns: 'admin' }
                )}
                required
                {...form.getInputProps('lastName')}
              />
            </Group>
            <TextInput
              label={t('userManagement.createUser.email', { ns: 'admin' })}
              placeholder={t('userManagement.createUser.emailPlaceholder', {
                ns: 'admin',
              })}
              type="email"
              required
              {...form.getInputProps('email')}
            />
            <TextInput
              label={t('userManagement.createUser.specialization', {
                ns: 'admin',
              })}
              placeholder={t(
                'userManagement.createUser.specializationPlaceholder',
                { ns: 'admin' }
              )}
              {...form.getInputProps('specialization')}
            />
            <Select
              label={t('userManagement.createUser.role', { ns: 'admin' })}
              placeholder={t('userManagement.createUser.rolePlaceholder', {
                ns: 'admin',
              })}
              required
              data={[
                {
                  value: 'practitioner',
                  label: t('userManagement.createUser.roles.practitioner', {
                    ns: 'admin',
                  }),
                },
                {
                  value: 'admin',
                  label: t('userManagement.createUser.roles.admin', {
                    ns: 'admin',
                  }),
                },
                {
                  value: 'lab_technician',
                  label: t('userManagement.createUser.roles.labTechnician', {
                    ns: 'admin',
                  }),
                },
              ]}
              {...form.getInputProps('role')}
            />
            <div>
              <Text size="sm" fw={500} mb={5}>
                Access Policy
              </Text>
              {loadingPolicies ? (
                <Group>
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">
                    Loading access policies...
                  </Text>
                </Group>
              ) : (
                <Select
                  placeholder="Select an access policy for this user"
                  data={accessPolicies.map((policy) => ({
                    value: policy.id || '',
                    label: policy.name || policy.id || 'Unknown Policy',
                  }))}
                  searchable
                  clearable
                  {...form.getInputProps('accessPolicy')}
                />
              )}
              <Text size="xs" c="dimmed" mt={5}>
                Access policy determines what actions this user can perform in
                the system
              </Text>
            </div>
            <FormActionButtons
              ref={formActionsRef}
              onDiscard={handleDiscardChanges}
              onClose={handleClose}
              hasUnsavedChanges={hasUnsavedChanges()}
              isLoading={loading}
              saveLabel={t('userManagement.createUser.create', { ns: 'admin' })}
            />
          </Stack>
        </form>
      </Modal>
    </>
  );
};

export default CreateInternalUserModal;
